/**
 * useDocumentMeta — sets <title>, meta description, canonical, hreflang alternates
 * and basic Open Graph tags for the current page. Lightweight, no router/helmet needed.
 *
 * Usage:
 *   useDocumentMeta({
 *     title: 'Page title',
 *     description: '...',
 *     canonical: '/araclar/ai-gorunurluk-checkup',     // path or full URL
 *     alternates: { tr: '/araclar/...', en: '/tools/...' },
 *     ogImage: '/og/cover.jpg' (optional),
 *   });
 */
import { useEffect } from 'react';

const FRONTEND = (process.env.REACT_APP_FRONTEND_URL || window.location.origin).replace(/\/$/, '');

const setNamedMeta = (name, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setPropMeta = (property, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href, attrs = {}) => {
  if (!href) return null;
  const selectorAttrs = Object.entries(attrs)
    .map(([k, v]) => `[${k}="${v}"]`)
    .join('');
  const selector = `link[rel="${rel}"]${selectorAttrs}`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
};

const toAbs = (path) => {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  return `${FRONTEND}${path.startsWith('/') ? '' : '/'}${path}`;
};

export default function useDocumentMeta({ title, description, canonical, alternates, ogImage, ogKind }) {
  useEffect(() => {
    if (title) document.title = title;
    setNamedMeta('description', description);

    const canonicalAbs = toAbs(canonical);
    if (canonicalAbs) setLink('canonical', canonicalAbs);

    // OG
    setPropMeta('og:title', title);
    setPropMeta('og:description', description);
    setPropMeta('og:type', 'website');
    if (canonicalAbs) setPropMeta('og:url', canonicalAbs);

    // OG image: explicit image wins; otherwise auto-generate one via /api/og
    // using the page title + ogKind (blog|tool|landing). This gives every
    // page a unique branded preview image with NO manual asset creation.
    const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    let imgUrl = ogImage ? toAbs(ogImage) : null;
    if (!imgUrl && apiBase && title) {
      const params = new URLSearchParams({ title, kind: ogKind || 'landing' });
      if (description) params.set('subtitle', description.slice(0, 120));
      imgUrl = `${apiBase}/api/og?${params.toString()}`;
    }
    if (imgUrl) {
      setPropMeta('og:image', imgUrl);
      setPropMeta('og:image:width', '1200');
      setPropMeta('og:image:height', '630');
    }

    // Twitter
    setNamedMeta('twitter:card', 'summary_large_image');
    setNamedMeta('twitter:title', title);
    setNamedMeta('twitter:description', description);
    if (imgUrl) setNamedMeta('twitter:image', imgUrl);

    // hreflang alternates — remove old auto-managed ones first
    document.head.querySelectorAll('link[rel="alternate"][data-auto-meta="1"]').forEach((n) => n.remove());
    if (alternates) {
      Object.entries(alternates).forEach(([lang, path]) => {
        const href = toAbs(path);
        if (!href) return;
        const el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', lang);
        el.setAttribute('href', href);
        el.setAttribute('data-auto-meta', '1');
        document.head.appendChild(el);
      });
    }
  }, [title, description, canonical, JSON.stringify(alternates || {}), ogImage]);
}
